import * as Phaser from "phaser";
import { CarpetDecorObject } from "$/game/prefabs/home/CarpetDecorObject.ts";
import { PictureDecorObject } from "$/game/prefabs/home/PictureDecorObject.ts";
import { FlowerDecorObject } from "$/game/prefabs/home/FlowerDecorObject.ts";
import { ClosetDecorObject } from "$/game/prefabs/home/ClosetDecorObject.ts";
import { BasicScene } from "$/game/prefabs/scenes/BasicScene.ts";

// UI счётчика (UI константы можно оставить здесь)
const COUNTER_FONT_SIZE = "24px";
const COUNTER_COLOR = "#ffffff";
const COUNTER_STROKE = "#000000";
const COUNTER_STROKE_THICKNESS = 4;
const PHYSICS_DEBUG = false;

export class EggBrakeScene extends BasicScene {
  private egg!: Phaser.Physics.Matter.Sprite;
  private groundBody!: MatterJS.BodyType;
  private leftWallBody!: MatterJS.BodyType;
  private rightWallBody!: MatterJS.BodyType;
  private counterText!: Phaser.GameObjects.Text;
  private isLaunched: boolean = false;
  private tapCount: number = 0;
  private targetTaps: number = 5; // будет переопределено в create
  private isCompleted: boolean = false;

  // Use any type to avoid typing issues
  private config: any;

  private gameCenterX: number = 0;
  private gameCenterY: number = 0;

  constructor() {
    super({
      key: "EggBrakeScene",
      physics: {
        matter: {
          gravity: { y: 1.5 }, // будет переопределена в create
          debug: PHYSICS_DEBUG,
        },
      },
    } as Phaser.Types.Scenes.SettingsConfig);
  }

  private createDecorItems(): void {
    const carpetDecorObject = new CarpetDecorObject(
      this,
      this.gameCenterX,
      this.gameCenterY,
    );
    this.add.existing(carpetDecorObject);

    const pictureDecorObject = new PictureDecorObject(
      this,
      this.gameCenterX - 170,
      this.gameCenterY - 600,
    );
    this.add.existing(pictureDecorObject);

    const closetDecorObject = new ClosetDecorObject(
      this,
      this.gameCenterX + 230,
      this.gameCenterY - 180,
    );
    this.add.existing(closetDecorObject);

    const flowerDecorObject = new FlowerDecorObject(
      this,
      closetDecorObject.x - 30,
      closetDecorObject.y - 130,
    );
    this.add.existing(flowerDecorObject);
  }

  create(): void {
    // this.soundManager.playMusic(this.keys.assets.sounds.music.home);

    this.gameCenterX = this.cameras.main.width / 2;
    this.gameCenterY = this.cameras.main.height * 0.8;

    this.add.image(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
      this.atlas,
      this.keys.assets.sprites.backgrounds.backgroundRoom,
    )
      .setDisplaySize(this.cameras.main.width, this.cameras.main.height)
      .setDepth(-1);

    this.createDecorItems();

    const { width, height } = this.scale;

    // На всякий случай убеждаемся, что ввод включен для этой сцены
    this.input.enabled = true;

    // Загружаем конфигурацию мини-игры и сохраняем как свойство класса
    this.config = (this.global.TUNER_TWA_CONFIG.game as any).miniGames.eggBrake;
    this.targetTaps = this.config.targetTaps;

    // Сброс состояния игры на каждое создание сцены
    this.isLaunched = false;
    this.tapCount = 0;
    this.isCompleted = false;

    // Включаем гравитацию Matter (в конфиге она 0)
    this.matter.world.setGravity(0, this.config.physics.gravityY);

    this.groundBody = this.matter.add.rectangle(
      this.gameCenterX,
      this.gameCenterY + this.config.ground.height / 2,
      width,
      this.config.ground.height,
      {
        isStatic: true,
        label: "ground",
      },
    );

    // Боковые стены
    const wallThickness = 40;
    const wallHeight = height; // перекрывает всю высоту сцены
    this.leftWallBody = this.matter.add.rectangle(
      0 - wallThickness / 2,
      height / 2,
      wallThickness,
      wallHeight,
      { isStatic: true, label: "left-wall" },
    );
    this.rightWallBody = this.matter.add.rectangle(
      width + wallThickness / 2,
      height / 2,
      wallThickness,
      wallHeight,
      { isStatic: true, label: "right-wall" },
    );

    // Создаем яйцо как физическое тело (спрайт для возможности смены кадров)
    this.egg = this.matter.add.sprite(
      this.gameCenterX,
      this.gameCenterY,
      this.atlas,
      this.keys.assets.sprites.egg.egg,
    );

    this.egg.setPosition(
      this.gameCenterX,
      this.gameCenterY - this.egg.displayHeight / 2,
    );

    this.egg.setScale(this.config.physics.eggScale);
    // Делаем тело максимально похожим на овал/кружок
    const radius = (this.egg.displayWidth + this.egg.displayHeight) / 4; // усредненный радиус
    this.egg.setCircle(radius);
    this.egg.setFriction(this.config.physics.eggFriction);
    this.egg.setFrictionAir(this.config.physics.eggFrictionAir);
    this.egg.setBounce(this.config.physics.eggBounce);
    // До первого тапа яйцо «заморожено»
    this.egg.setStatic(true);

    this.egg.setInteractive({ useHandCursor: true });

    // Текст счётчика
    this.counterText = this.add.text(
      width / 2,
      159,
      this.formatCounter(this.tapCount, this.targetTaps),
      {
        fontSize: "43px",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    );
    this.counterText.setOrigin(0.5);

    // Обработчик клика по яйцу: если тап слева от центра — толкаем вправо, и наоборот
    this.egg.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (this.isCompleted) return;
      // Игровое взаимодействие всегда активно
      const pointerX = pointer.worldX ?? pointer.x;
      const eggX = this.egg.x;
      const dx = pointerX - eggX;
      const pushRight = dx < 0; // тап слева — толкнуть вправо

      // Первый тап «размораживает» яйцо
      if (!this.isLaunched) {
        this.isLaunched = true;
        this.egg.setStatic(false);
      }

      const body = this.egg.body as MatterJS.BodyType;
      const currentVx = body.velocity.x;
      const currentVy = body.velocity.y;

      // «Слепая зона» — небольшие промахи по центру не дают горизонтального толчка
      const tapConfig = this.config.tap;
      const deadZone = Math.max(
        tapConfig.deadZoneMinPx,
        this.egg.displayWidth * tapConfig.deadZoneWidthFactor,
      );
      let horizontalBoost = 0;
      if (Math.abs(dx) > deadZone) {
        // Плавная сила толчка по мере удаления от центра (за пределами deadZone)
        const maxReach = Math.max(
          deadZone + 1,
          this.egg.displayWidth * tapConfig.maxReachFactor,
        );
        const normalized = Phaser.Math.Clamp(
          (Math.abs(dx) - deadZone) / (maxReach - deadZone),
          0,
          1,
        );
        const base = tapConfig.horizontalMinBoost +
          (tapConfig.horizontalMaxBoost - tapConfig.horizontalMinBoost) *
            normalized;
        horizontalBoost = (pushRight ? 1 : -1) * base;
      }

      // Вертикальный подброс: добавочный импульс вверх + гарантированный минимум
      const vyCandidate = currentVy - 2 * tapConfig.verticalBoostMultiplier;
      const newVy = Math.min(vyCandidate, tapConfig.minUpwardVy);

      this.egg.setVelocity(currentVx + horizontalBoost, newVy);
      // Легкое кручение пропорционально силе толчка
      const spin = Phaser.Math.Clamp(
        horizontalBoost * tapConfig.spinFactor,
        -tapConfig.spinClamp,
        tapConfig.spinClamp,
      );
      this.egg.setAngularVelocity(spin);

      // Учитываем тап после применения импульса
      this.tapCount += 1;
      this.updateEggSprite();
      this.counterText.setText(
        this.formatCounter(this.tapCount, this.targetTaps),
      );
      if (this.tapCount >= this.targetTaps) {
        this.winGame();
      }
    });
  }

  // Метод подбрасывания больше не используется — управление осуществляется на каждом тапе
  override update(time: number, delta: number): void {
    super.update(time, delta);

    if (this.isCompleted || !this.isLaunched) return;

    // Проигрыш при выходе за пределы видимой сцены
    const { width: _width, height: _height } = this.scale;
    const _x = this.egg.x; // оставлено для возможной отладки
    const _y = this.egg.y;
    // Больше не сбрасываем прогресс при вылете вверх — только по касанию пола/стен
  }

  private resetCounterOnly(): void {
    this.tapCount = 0;
    this.updateEggSprite();
    this.counterText.setText(
      this.formatCounter(this.tapCount, this.targetTaps),
    );
  }

  private updateEggSprite(): void {
    // Последние три тапа показывают трещины: 3-й тап = brake-1, 4-й = brake-2, 5-й = brake-3
    if (this.tapCount <= 2) {
      this.egg.setTexture(this.atlas, this.keys.assets.sprites.egg.egg);
    } else if (this.tapCount === 3) {
      this.egg.setTexture(this.atlas, this.keys.assets.sprites.egg.eggBrake1);
    } else if (this.tapCount === 4) {
      this.egg.setTexture(this.atlas, this.keys.assets.sprites.egg.eggBrake2);
    } else if (this.tapCount >= 5) {
      this.egg.setTexture(this.atlas, this.keys.assets.sprites.egg.eggBrake3);
    }
  }

  private winGame(): void {
    const { width, height } = this.scale;

    this.isCompleted = true;

    // Удаляем со сцены яйцо и счётчик
    if (this.counterText) {
      this.counterText.destroy();
    }
    if (this.egg) {
      // тут надо создать части яйца
      // Получаем текущую скорость и вращение яйца
      const eggBody = this.egg.body as MatterJS.BodyType;
      const eggVx = eggBody.velocity.x;
      const eggVy = eggBody.velocity.y;
      const eggAngularVelocity = eggBody.angularVelocity || 0;

      // Скорости разлета половинок
      const spreadSpeed = 8; // горизонтальная скорость разлета
      const upwardBoost = -2; // небольшой подброс вверх

      // Use this.config instead of config
      const physicsConfig = this.config.physics;
      const eggPart1 = this.matter.add.sprite(
        this.egg.x,
        this.egg.y,
        this.atlas,
        this.keys.assets.sprites.egg.eggPart1,
      );
      eggPart1.setPosition(this.egg.x - this.egg.displayWidth / 2, this.egg.y);
      eggPart1.setScale(physicsConfig.eggScale * 0.8);
      eggPart1.setRotation(this.egg.rotation);
      eggPart1.setStatic(false);
      eggPart1.setCircle((this.egg.displayWidth + this.egg.displayHeight) / 8);
      eggPart1.setFriction(physicsConfig.eggFriction);
      eggPart1.setFrictionAir(physicsConfig.eggFrictionAir);
      eggPart1.setBounce(0.7);
      // Разлет влево с вращением
      eggPart1.setVelocity(eggVx - spreadSpeed, eggVy + upwardBoost);
      eggPart1.setAngularVelocity(eggAngularVelocity - 0.15);

      const eggPart2 = this.matter.add.sprite(
        this.egg.x,
        this.egg.y,
        this.atlas,
        this.keys.assets.sprites.egg.eggPart2,
      );
      eggPart2.setPosition(this.egg.x + this.egg.displayWidth / 2, this.egg.y);
      eggPart2.setScale(physicsConfig.eggScale * 0.8);
      eggPart2.setRotation(this.egg.rotation);
      eggPart2.setStatic(false);
      eggPart2.setCircle((this.egg.displayWidth + this.egg.displayHeight) / 8);
      eggPart2.setFriction(physicsConfig.eggFriction);
      eggPart2.setFrictionAir(physicsConfig.eggFrictionAir);
      eggPart2.setBounce(0.7);
      // Разлет вправо с вращением
      eggPart2.setVelocity(eggVx + spreadSpeed, eggVy + upwardBoost);
      eggPart2.setAngularVelocity(eggAngularVelocity + 0.15);

      // а тут надо создать пета
      const petFrame = this.keys.assets.sprites
        .pets[
          this.registry.get("pet")
            ?.type as keyof typeof this.keys.assets.sprites.pets
        ].normal;
      const pet = this.matter.add.sprite(
        this.egg.x,
        this.egg.y,
        this.atlas,
        petFrame,
      );
      pet.setPosition(this.egg.x, this.egg.y);
      pet.setStatic(false);

      this.egg.destroy();
    }

    const jsConfetti = new (window as any).JSConfetti();
    jsConfetti.addConfetti();

    // this.sound.play(this.keys.assets.sounds.win, { volume: 0.5 });

    setTimeout(() => {
      this.transition.toScene(
        "PetHomeScene",
        this.keys.strings.sceneTransitions.home,
      );
      jsConfetti.destroyCanvas();
    }, 2000);
  }

  private formatCounter(current: number, goal: number): string {
    return `TAP! ${current} / ${goal}`;
  }
}
